/**
 * Tokens semânticos da Dashboard v2 — apelidos de leitura por cima de
 * `theme.colors.designSystem` (a paleta canônica da Jusfy). Nenhuma cor
 * sólida é declarada aqui: cor que faltava entrou no design system
 * (`src/app/styles/theme/colors/index.js`), e este arquivo só dá o nome
 * semântico que a tela usa (o mesmo papel dos tokens escopados de
 * `EditReport/theme/tokens.ts`).
 *
 * Regra semântica da referência visual (prefixo `--jd-`): verde = identidade ·
 * laranja = prazo correndo/atenção · vermelho = risco · ROXO = IA e só IA ·
 * azul/amarelo = fase do atendimento. Não usar roxo em status.
 *
 * As únicas strings literais são degradês e sombras (composições exclusivas
 * desta tela, com stops intermediários sem equivalente no design system) —
 * mesmo precedente de `shadows` no tema do EditReport.
 */
import colors from 'app/styles/theme/colors';
import typography from 'app/styles/theme/typography';

const ds = colors.designSystem;

export const jd = {
  // Superfícies, da mais funda para a mais alta.
  pageBg: ds.gray[100],
  surface: ds.gray[0],

  // Fios.
  border: ds.gray[250],
  borderStrong: ds.gray[400],
  /** Fio divisor entre colunas de conteúdo — o `stroke/neutral/disabled` dos
   *  handoffs, um passo acima de `borderStrong`. */
  borderDivider: ds.gray[500],

  // Tinta, do mais forte ao mais apagado.
  text: ds.gray[950],
  text2: ds.gray[700],
  text3: ds.gray[600],
  text4: ds.gray[400],

  // Verde da Jusfy. `primaryStrong` é o verde legível SOBRE menta (AA).
  primary: ds.green[500],
  primaryHover: ds.green[600],
  primaryStrong: ds.green[900],
  primaryContrast: ds.gray[0],

  // Menta: superfícies verdes e fios delas.
  mint: ds.green[100],
  mintFaint: ds.green[50],
  /** Fundo do painel de empty state (`--verde-empty` do protótipo). */
  emptyStateSurface: ds.green[75],
  /** Near-white esverdeado — o mesmo do fundo do sidebar. */
  mintTint: colors.sidebar.background,
  mintStrong: ds.green[150],
  mintBorder: ds.green[150],
  mintBorderStrong: ds.green[200],

  // Laranja: atenção e prazo correndo.
  orange: ds.orange[500],
  orangeHover: ds.orange[600],
  orangeSoft: ds.orange[100],
  orangeBorder: ds.orange[200],

  // Roxo: a cor da IA, e SÓ dela.
  purple: ds.purple[400],
  purpleHover: ds.purple[600],
  purpleContrast: ds.gray[0],

  // Azul: fase do atendimento (tag "Veio do JusProcessos").
  blueSoft: ds.blue[50],
  blueStrong: ds.blue[700],

  // Ouro das conquistas e vermelho de risco.
  gold: ds.yellow[600],
  danger: ds.red[500],
  dangerSoft: ds.red[50],

  // Sombras: cards (green[900] a 45%) e popovers (gray[950] a 32%, ProfileMenu).
  cardShadow: '0 18px 40px -28px rgba(1, 86, 63, 0.45)',
  overlayShadow: '0 24px 56px -24px rgba(19, 19, 19, 0.32)',

  // Fundo do console de IA (glow verde = green[500] a 11%).
  consoleBg: `radial-gradient(112% 88% at 50% -14%, #E4F6EF 0%, ${ds.green[50]} 44%, #FCFDFF 78%)`,
  consoleBorder: ds.gray[250],
  consoleGlow: 'rgba(1, 171, 125, 0.11)',

  // Superfície tintada (degradê que dá volume sem sombra) e cartão de vidro.
  mintSurface: 'linear-gradient(168deg, #E9F8F3 0%, #F7FCFB 100%)',
  glassBorder: ds.gray[250],

  // Fundos dos banners de campanha.
  bannerMint: `linear-gradient(168deg, #E9F8F3 0%, #F7FCFB 52%, ${ds.gray[0]} 100%)`,
  bannerAccounting: `linear-gradient(231deg, #F8CAB7 -1.9%, ${ds.gray[0]} 137.45%)`,
  bannerAi: ds.green[100],
  /** Fundo do banner de cartão das novas taxas (handoff `2:1840`). Degradê
   *  HORIZONTAL: amostrado no render, a cor não varia com a altura — só o
   *  pêssego da direita é mais saturado que o da esquerda. */
  bannerPayCard: 'linear-gradient(90deg, #FAE4DA 0%, #F8D3C3 100%)',
} as const;

/** Fonte da tela — `Noto Sans`, a dominante do design system. */
export const JD_FONT = `'${typography.quartenary}', sans-serif`;
